import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { Button } from "../../Components/Button";

import { cancelAPIRequest, classNames, getErrorMessages } from "../../Utils";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearBusinessTax,
  clearBusinessTaxError,
  requestAddMainBusiness,
  requestDeleteBusinessTax,
  requestListBusiness,
  requestListBusinessTax,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { Pagination } from "../../Components/Pagination";
import TableSkeleton from "../../Components/TableSkeleton";
import { PencilAltIcon, TrashIcon } from "@heroicons/react/outline";
import {
  useBusinessTaxDispatch,
  useBusinessTaxState,
} from "../../Context/tax/context";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { TAX_COMPUTATION, TAX_TYPE } from "../../Constants";
import { ModalAlert } from "../../Components/ModalAlert";
import { isBusinessMainBlocked } from "../../Constants/business";

export const TaxList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();

  const [selectedBusiness, setselectedBusiness] = useState({});
  const [page, setpage] = useState(1);
  const [taxId, settaxId] = useState("");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const businessTransactionDispatch = useBusinessTransactionDispatch();
  const businessTaxDispatch = useBusinessTaxDispatch();
  const authDispatch = useAuthDispatch();

  const { businesses } = useBusinessTransactionState();

  const {
    request: businessTaxRequest,
    businessTaxes,
    businessTaxesLoading,
    businessTaxNextTokens,
    businessTaxesError,
    deletedID,
    deleteTaxLoading,
    deleteTaxError,
  } = useBusinessTaxState();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");

  useEffect(() => {
    clearBusinessTax(businessTaxDispatch);
    clearBusinessTaxError(businessTaxDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessTransactionDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessTransactionDispatch, user.id]);

  useEffect(() => {
    // Function to set selected business
    const setSelectedBusinessLogic = async () => {
      if (businessIdParam) {
        if (businesses.length > 0) {
          const business = businesses.find(
            (item) => item.value === businessIdParam
          );
          if (business) {
            setselectedBusiness({
              ...business,
              label: business.name,
              value: business.id,
            });
          } else {
            // Handle if business is not found, select index 0
            setselectedBusiness({
              ...businesses[0],
              label: businesses[0].name,
              value: businesses[0].id,
            });
          }
        } else {
          // Handle if businesses are empty, no business to select
        }
      } else if (user.mainBusiness) {
        // Set selectedBusiness directly from user.mainBusiness
        setselectedBusiness({
          ...user.mainBusiness,
          label: user.mainBusiness.name,
          value: user.mainBusiness.id,
        });
      } else if (businesses.length > 0) {
        // Set selected business to the first item in the list
        // Prefer a healthy business: a RESTRICTED / PENDING_DELETION one must
        // not become the account-wide main business. Falls back to the first
        // entry when every business is blocked.
        const mainCandidate =
          businesses.find((business) => !isBusinessMainBlocked(business)) ||
          businesses[0];
        setselectedBusiness({
          ...mainCandidate,
          label: mainCandidate.name,
          value: mainCandidate.id,
        });
        requestAddMainBusiness(authDispatch, {
          businessID: mainCandidate.id,
        });
      } else {
        // Handle if businesses are empty, no business to select
      }
    };

    setSelectedBusinessLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses, businessIdParam, user.mainBusiness]);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTaxDispatch, selectedBusiness.value]);

  useEffect(() => {
    if (deleteTaxError) {
      setShowErrorModal(true);
      setShowConfirmModal(false);
    }
  }, [deleteTaxError]);

  useEffect(() => {
    if (!deletedID) {
      return;
    }
    setShowConfirmModal(false);
    setShowSuccessModal(true);
  }, [deletedID]);

  const onDelete = () => {
    requestDeleteBusinessTax(businessTaxDispatch, {
      id: taxId,
    });
  };

  const onCreate = () => {
    clearBusinessTax(businessTaxDispatch);
    navigate(`/tax/create?id=${selectedBusiness.value}`);
  };

  const onLoad = () => {
    if (businessTaxRequest) {
      cancelAPIRequest(businessTaxRequest);
    }
    if (selectedBusiness.value) {
      requestListBusinessTax(businessTaxDispatch, {
        page: 1,
        businessID: selectedBusiness.value,
      });
    }
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (businessTaxNextTokens[nextPage]) {
      requestListBusinessTax(businessTaxDispatch, {
        page: nextPage,
        limit: 10,
        businessID: selectedBusiness.value,
        nextTokens: businessTaxNextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListBusinessTax(businessTaxDispatch, {
        page: prevPage,
        businessID: selectedBusiness.value,
        nextTokens: businessTaxNextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (businessTaxNextTokens[selectedPage] || selectedPage === 1) {
      requestListBusinessTax(businessTaxDispatch, {
        page: selectedPage,
        businessID: selectedBusiness.value,
        nextTokens: businessTaxNextTokens[selectedPage],
      });
    }
  };

  return (
    <div className="space-y-4">
      {deleteTaxError && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setShowErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(deleteTaxError)}
          onConfirm={() => clearBusinessTaxError(businessTaxDispatch)}
        />
      )}
      <ModalConfirm
        open={showSuccessModal}
        type="SUCCESS"
        setOpen={(open) => {
          setShowSuccessModal(open);
        }}
        title="Success"
        description="The tax has been successfully deleted"
        onConfirm={() => clearBusinessTax(businessTaxDispatch)}
      />
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setShowConfirmModal(open);
        }}
        title="Delete Tax"
        description="Taxes can only be deleted if they have not been used in any transaction. Continue?"
        confirmButtonText="Yes"
        cancelButtonText="Cancel"
        loadingButton={deleteTaxLoading}
        disabledButton={deleteTaxLoading}
        onConfirm={onDelete}
      />
      <div className="grid gird-cols-1 sm:grid-cols-2 gap-y-4 mb-4">
        <div className="max-w-md md:max-w-2xl">
          <Listbox value={selectedBusiness} onChange={setselectedBusiness}>
            {({ open }) => (
              <>
                <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                  <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                    {selectedBusiness?.label || t("Business Name")}
                  </span>
                  <SelectorIcon className="w-5 h-5 text-gray-500" />
                </ListboxButton>
                <Transition
                  show={open}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-auto rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {businesses.map((business) => (
                      <ListboxOption
                        key={business.value}
                        className={({ focus }) =>
                          classNames(
                            focus ? "text-white bg-blue-600" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-3 pr-9"
                          )
                        }
                        value={business}
                      >
                        {({ selected, focus }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "ml-3 mr-2 block truncate"
                                )}
                              >
                                {business.label}
                              </span>
                              {selected ? (
                                <span
                                  className={classNames(
                                    focus ? "text-white" : "text-blue-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              ) : null}
                            </div>
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
        <div className="gap-4 flex justify-start sm:justify-end w-full">
          <Button
            className="w-full sm:w-auto"
            type="button"
            onClick={onCreate}
            title={t("Create New Tax")}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Name")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Computation")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Type")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Amount")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Action")}
                    </th>
                  </tr>
                </thead>
                {businessTaxesLoading ? (
                  <TableSkeleton rows={10} columns={5} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {businessTaxes.length > 0 ? (
                      businessTaxes.map((tax) => {
                        const titleCaseComputation =
                          TAX_COMPUTATION[tax.computation] || tax.computation;
                        const titleCaseType = TAX_TYPE[tax.type] || tax.type;

                        return (
                          <tr key={tax.id}>
                            <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                              {tax.name}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {t(titleCaseComputation)}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {t(titleCaseType)}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              <NumericFormat
                                displayType={"text"}
                                value={tax.amount}
                                thousandSeparator={
                                  tax?.computation === "FIXED" ? "." : ""
                                }
                                decimalSeparator={","}
                                prefix={
                                  tax?.computation === "FIXED" ? "Rp " : ""
                                }
                                suffix={tax?.computation === "FIXED" ? "" : "%"}
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              <div className="flex flex-auto gap-2">
                                <button
                                  onClick={() => {
                                    navigate(`/tax/edit/${tax.id}`);
                                  }}
                                >
                                  <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                </button>
                                <button
                                  onClick={() => {
                                    setShowConfirmModal(true);
                                    settaxId(tax.id);
                                  }}
                                >
                                  <TrashIcon className="h-5 w-5 text-red-500" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="p-4" colSpan={6}>
                          {businessTaxesError ? (
                            <ErrorList
                              message={
                                businessTaxesError?.message ||
                                "Error fetching taxes."
                              }
                              errors={businessTaxesError?.errors}
                            />
                          ) : (
                            <EmptyList title="No Taxes" />
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={businessTaxNextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
